import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import StepIndicator from './StepIndicator';
import { ApplicationStatus, WizardStepId } from '../../types/wizard';
import { calculateStepStatuses, canNavigateToStep } from '../../utils/wizardSteps';

interface WizardContainerProps {
  children: React.ReactNode;
  currentStepId: WizardStepId;
  applicationStatus: ApplicationStatus;
  onStepChange?: (stepId: WizardStepId) => void;
}

const WizardContainer: React.FC<WizardContainerProps> = ({
  children,
  currentStepId,
  applicationStatus,
  onStepChange
}) => {
  const navigate = useNavigate();

  // Calculate step statuses
  const steps = calculateStepStatuses(
    currentStepId,
    applicationStatus.applicantStep,
    Boolean(applicationStatus.applicationId)
  );

  // Handle step click from indicator
  const handleStepClick = (stepId: string) => {
    const targetStepId = stepId as WizardStepId;

    // V2 steps are server-controlled and single-submit. Keep applicants on
    // the authoritative current step instead of reopening completed forms.
    if (applicationStatus.applicationId && targetStepId !== currentStepId) {
      return;
    }

    // Check if navigation is allowed
    if (!canNavigateToStep(targetStepId, currentStepId, steps)) {
      return;
    }

    // Navigate to the step
    if (onStepChange) {
      onStepChange(targetStepId);
    } else {
      // Default navigation
      const legacyToken = applicationStatus.documentToken;
      navigate(`/wizard/${targetStepId}${legacyToken ? `?token=${encodeURIComponent(legacyToken)}` : ''}`);
    }
  };

  return (
    <div className="app-shell">
      <Header />

      <main className="app-main">
        {/* Applicant info banner */}
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-slate">Courier application</div>
            <div className="font-semibold text-charcoal">
              {applicationStatus.name ? `Welcome back, ${applicationStatus.name}` : 'Complete your next step'}
            </div>
          </div>
          {applicationStatus.deliveryArea && (
            <div className="inline-flex w-fit items-center rounded-full bg-logo-green/10 px-3 py-1.5 text-xs font-semibold text-shadow-green">
              {applicationStatus.deliveryArea} market
            </div>
          )}
        </div>

        {/* Step indicator */}
        <StepIndicator
          steps={steps}
          currentStepId={currentStepId}
          onStepClick={handleStepClick}
        />

        {/* Step content */}
        <div className="animate-fade-in">
          {children}
        </div>

        {/* Help section */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500">
            Questions about this step?{' '}
            <a
              href="mailto:relations@valleyeats.ca"
              className="text-logo-green hover:text-shadow-green font-semibold"
            >
              Contact us
            </a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WizardContainer;
